import type { AxiosResponse } from "axios"
import { downloadZip } from "client-zip"

function createDownloadLink(blob: Blob, filename: string): void {
  const link = document.createElement("a")
  link.href = URL.createObjectURL(blob)
  link.download = filename
  link.click()
  link.remove()
  URL.revokeObjectURL(link.href)
}

function filenameFromResponse(r: AxiosResponse): string {
  const contentDisposition = r.headers["content-disposition"]

  // https://stackoverflow.com/questions/40939380/how-to-get-file-name-from-content-disposition

  const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i
  const asciiFilenameRegex = /filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i

  if (utf8FilenameRegex.test(contentDisposition)) {
    const filename_utf8 = utf8FilenameRegex.exec(contentDisposition)![1]
    return decodeURIComponent(filename_utf8)
  } else {
    const matches = asciiFilenameRegex.exec(contentDisposition)
    if (matches != null && matches[2]) {
      return matches[2]
    }
  }

  throw new Error(`Could not find filename in content-disposition: ${contentDisposition}`)
}

// MIME types the browser can render inline (in a new tab / iframe). Anything not
// listed falls back to application/pdf to preserve the historical behavior where
// the /content endpoint returns a generic octet-stream forced to render as a PDF.
const INLINE_VIEW_MIME_TYPES: Record<string, string> = {
  pdf: "application/pdf",
  xml: "application/xml",
}

export function inlineViewMimeType(filename: string): string {
  const extension = filename.split(".").pop()?.toLowerCase()
  return (extension && INLINE_VIEW_MIME_TYPES[extension]) || "application/pdf"
}

export async function dispatchFileResponses(responses: AxiosResponse[]) {
  if (responses.length === 1) {
    const resp = responses[0]
    createDownloadLink(resp.data, filenameFromResponse(resp))
  } else {
    const files: File[] = R.map(
      responses,
      (resp) => new File([resp.data], filenameFromResponse(resp)),
    )
    const blob = await downloadZip(files).blob()
    createDownloadLink(blob, "files.zip")
  }
}
